import { Injectable } from '@angular/core';
import { Subject, fromEvent, Observable, of } from 'rxjs';
import { flatMap, map, take, zip } from 'rxjs/operators';
import { DomSanitizer } from '@angular/platform-browser';
import * as _ from 'lodash';
import { AppConstants } from '../../utils/appConstants.class';
import { ApplicationType } from '../../utils/enums';

@Injectable({
  providedIn: 'root'
})
export class IndexedDbService {
  private request: IDBOpenDBRequest;
  private db: any;
  private dbSubject = new Subject();

  private stringValues = [AppConstants.INDEXEDDB_TABLES.languages];

  private noKeyValues = [
    AppConstants.INDEXEDDB_TABLES.languages,
    AppConstants.INDEXEDDB_TABLES.images,
    AppConstants.INDEXEDDB_TABLES.translations
  ];

  private optimizationV1 = [
    { name: AppConstants.INDEXEDDB_TABLES.trainees, keyPath: 'Value' },
    { name: AppConstants.INDEXEDDB_TABLES.moments, keyPath: 'Value' },
    { name: AppConstants.INDEXEDDB_TABLES.courses, keyPath: 'Value' }
  ];

  private v1 = [
    { name: AppConstants.INDEXEDDB_TABLES.inspections, keyPath: 'id' },
    { name: AppConstants.INDEXEDDB_TABLES.permissions, keyPath: 'Value' },
    { name: AppConstants.INDEXEDDB_TABLES.people, keyPath: 'id' },
    { name: AppConstants.INDEXEDDB_TABLES.templates, keyPath: 'id' },
    { name: AppConstants.INDEXEDDB_TABLES.translations, keyPath: '' },
    { name: AppConstants.INDEXEDDB_TABLES.productLines, keyPath: 'Id' },
    { name: AppConstants.INDEXEDDB_TABLES.models, keyPath: 'Id' },
    { name: AppConstants.INDEXEDDB_TABLES.categories, keyPath: 'Id' },
    { name: AppConstants.INDEXEDDB_TABLES.images, keyPath: '' },
    { name: AppConstants.INDEXEDDB_TABLES.languages, keyPath: '' },
    { name: AppConstants.INDEXEDDB_TABLES.applicationTypes, keyPath: 'Value' },
    { name: AppConstants.INDEXEDDB_TABLES.actionPlans, keyPath: 'id' },
    { name: AppConstants.INDEXEDDB_TABLES.localEntities, keyPath: 'Label' },
    { name: AppConstants.INDEXEDDB_TABLES.countries, keyPath: 'Code' },
    { name: AppConstants.INDEXEDDB_TABLES.currencies, keyPath: 'Value' },
    { name: AppConstants.INDEXEDDB_TABLES.pluginModels, keyPath: 'PluginType' }
  ];

  constructor(private sanitizer: DomSanitizer) {}

  private _put(name, object, key?): Observable<any> {
    const store = this.db.transaction(name, 'readwrite').objectStore(name);
    let request = null;
    if (key && this.noKeyValues.includes(name)) {
      request = store.put(object, key);
    } else if (this.stringValues.includes(name)) {
      request = store.put(object, object);
    } else {
      request = store.put(object);
    }
    request.onerror = (event) => {
      console.error('failed to put data');
    };
    return fromEvent(request, 'success').pipe(take(1));
  }

  private _putImage(key, blob, isNew = false) {
    const object = { assetId: key, file: blob, isNew };
    const request = this.db
      .transaction(AppConstants.INDEXEDDB_TABLES.images, 'readwrite')
      .objectStore(AppConstants.INDEXEDDB_TABLES.images)
      .put(object, key);
    request.onerror = (event) => {};
    return fromEvent(request, 'success').pipe(take(1));
  }

  private _read(name, key): Observable<any> {
    const request = this.db.transaction(name, 'readonly').objectStore(name).get(key);
    request.onerror = (event) => console.error(`failed to read from ${name}`);
    return fromEvent(request, 'success').pipe(
      take(1),
      map((event) => request.result)
    );
  }

  private _readAll(name) {
    const request = this.db.transaction(name, 'readonly').objectStore(name).getAll();
    request.onerror = (event) => console.error(`failed to read all ${name}`);
    return fromEvent(request, 'success').pipe(
      take(1),
      map((event) => request.result)
    );
  }

  private _getImageUrl(key: string) {
    const request = this.db
      .transaction(AppConstants.INDEXEDDB_TABLES.images, 'readonly')
      .objectStore(AppConstants.INDEXEDDB_TABLES.images)
      .get(key);
    request.onerror = (event) => console.error(`failed to read image`);
    return fromEvent(request, 'success').pipe(
      take(1),
      map((event) => {
        const image = request?.result?.file;
        const url = window.URL;
        if (image) {
          return typeof image === 'string' ? image : this.sanitizer.bypassSecurityTrustUrl(url.createObjectURL(image));
        }
        return null;
      })
    );
  }

  private _remove(name: string, key: string): Observable<any> {
    const store = this.db.transaction(name, 'readwrite').objectStore(name);
    const request = store.delete(key);
    request.onerror = (event) => console.error(`failed to remove item`);
    return fromEvent(request, 'success').pipe(take(1));
  }

  public remove(name: string, key: string): Observable<any> {
    if (this.db) {
      return this._remove(name, key);
    } else {
      return this.dbSubject.pipe(
        flatMap((data) => {
          this.dbSubject.unsubscribe();
          return this._remove(name, key);
        })
      );
    }
  }

  public put(name, object, key?): Observable<any> {
    if (this.db) {
      return this._put(name, object, key);
    } else {
      return this.dbSubject.pipe(
        flatMap((data) => {
          this.dbSubject.unsubscribe();
          return this._put(name, object, key);
        })
      );
    }
  }

  public merge(name, newObject, filterScope = false): Observable<any> {
    return this.read(name, newObject.id).pipe(
      flatMap((oldObject) => {
        if (oldObject) {
          if (filterScope) {
            this.filterScope(oldObject, newObject.scope);
          }
          const obj = _.mergeWith(oldObject, newObject, (oldValue, newValue, key, origObject, source, stack) => {
            if (_.isArray(oldValue)) {
              if ((newValue && newValue.length > 0 && newValue[0].id) || (oldValue && oldValue.length > 0 && oldValue[0].id)) {
                if (key === 'allPlugins' || key === 'plugins') {
                  // if we are merging plugins, we don't want to remove the postServerUrl property, some editors may need it
                  return _.unionWith(newValue, oldValue, (arrVal, othVal) => {
                    if (!arrVal || !othVal) {
                      // prevent pathological cases and keep behavior similar to _.unionBy(_,_,'id')
                      return _.get(arrVal, 'id') === _.get(othVal, 'id');
                    }
                    if (arrVal.id !== othVal.id) {
                      return false;
                    }
                    if (!arrVal.postServerUrl && othVal.postServerUrl) {
                      arrVal.postServerUrl = othVal.postServerUrl;
                    }
                    // order of comparison is not guaranteed, let's handle both cases
                    if (arrVal.postServerUrl && !othVal.postServerUrl) {
                      othVal.postServerUrl = arrVal.postServerUrl;
                    }
                    return true;
                  });
                } else {
                  return _.unionBy(newValue, oldValue, 'id');
                }
              } else {
                return newValue;
              }
            }
          });
          return this.put(name, obj);
        } else {
          return this.put(name, newObject);
        }
      })
    );
  }

  public mergeAll(name, data: any[]): Observable<any> {
    const obs = [];
    data.forEach((d) => {
      obs.push(this.merge(name, d));
    });
    return of(null).pipe(zip(...obs));
  }

  public putImage(key, blob, isNew = false): Observable<any> {
    if (this.db) {
      return this._putImage(key, blob, isNew);
    } else {
      return this.dbSubject.pipe(flatMap((data) => this._putImage(key, blob, isNew)));
    }
  }

  public read<T = any>(name, key): Observable<T> {
    if (this.db) {
      return this._read(name, key);
    } else {
      return this.dbSubject.pipe(flatMap((data) => this._read(name, key)));
    }
  }

  public readAll(name): Observable<any> {
    if (this.db) {
      return this._readAll(name);
    } else {
      return this.dbSubject.pipe(flatMap((data) => this._readAll(name)));
    }
  }

  public getImageUrl(key: string): Observable<any> {
    if (this.db) {
      return this._getImageUrl(key);
    } else {
      return this.dbSubject.pipe(flatMap((data) => this._getImageUrl(key)));
    }
  }

  public clearTable(name: string) {
    const request = this.db.transaction(name, 'readwrite').objectStore(name).clear();
    request.onerror = (event) => {};
    return fromEvent(request, 'success').pipe(take(1));
  }

  public clearAll() {
    const obs = [];
    this.v1.forEach((v) => obs.push(this.clearTable(v.name)));
    return of(null).pipe(zip(...obs));
  }

  private createObjectStore(db: IDBDatabase, table: string, key?: string) {
    if (key) {
      db.createObjectStore(table, { keyPath: key });
    } else {
      db.createObjectStore(table);
    }
  }

  private filterScope(data, scope) {
    if (scope && scope.length > 0) {
      scope.forEach((s) => {
        if (data[s.property] && data[s.property].length > 0) {
          data[s.property] = data[s.property].filter((p) => s.ids.includes(p.id));
        }
      });
    }
  }

  public openDatabase(applicationType: ApplicationType, userId: string): Observable<any> {
    if (this.db) {
      return of(null);
    }

    switch (applicationType) {
      case ApplicationType.Maintenance:
        this.request = window.indexedDB.open(`${AppConstants.INDEXEDDB_MAINTENANCE_DB}_${userId}`, 1);
        break;

      case ApplicationType.Optimization:
        this.request = window.indexedDB.open(`${AppConstants.INDEXEDDB_OPTIMIZATION_DB}_${userId}`, 1);
        this.v1 = [...this.v1, ...this.optimizationV1];
        break;

      case ApplicationType.Installation:
        this.request = window.indexedDB.open(`${AppConstants.INDEXEDDB_INSTALLATION_DB}_${userId}`, 1);
        break;
    }

    this.request.onupgradeneeded = (event: any) => {
      const db: IDBDatabase = event.target.result;
      if (event.oldVersion < 1) {
        this.v1.forEach((v) => this.createObjectStore(db, v.name, v.keyPath));
      }
    };

    return of(null).pipe(
      zip(
        fromEvent(this.request, 'success').pipe(
          take(1),
          map((res) => {
            this.db = this.request.result;
            this.dbSubject.next(this.db);
            return res.returnValue;
          })
        )
      )
    );
  }
}
